import type { BaseSlideProps, InteractiveContent } from './SlideLayout.types';
import { inlineMarkdown } from '@/lib/markdown';

function renderMarkdownPrompt(text: string) {
  const lines = text.split('\n').filter(line => line.trim() !== '');
  const elements: JSX.Element[] = [];
  let bulletGroup: string[] = [];

  const flushBullets = () => {
    if (bulletGroup.length > 0) {
      elements.push(
        <ul key={`bullets-${elements.length}`} className="space-y-2 text-left my-4">
          {bulletGroup.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-lg text-slate-700 dark:text-slate-300">
              <span className="accent-text mt-1 text-sm flex-shrink-0">&#9679;</span>
              <span dangerouslySetInnerHTML={{ __html: inlineMarkdown(item) }} />
            </li>
          ))}
        </ul>
      );
      bulletGroup = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed.startsWith('•') || trimmed.startsWith('- ')) {
      const content = trimmed.replace(/^[•\-]\s*/, '');
      bulletGroup.push(content);
    } else {
      flushBullets();
      elements.push(
        <p
          key={`p-${elements.length}`}
          className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed mb-3 last:mb-0"
          dangerouslySetInnerHTML={{ __html: inlineMarkdown(trimmed) }}
        />
      );
    }
  }

  flushBullets();
  return elements;
}

export function InteractiveSlide({ title, content, accentColor }: BaseSlideProps) {
  const { prompt, type } = content as InteractiveContent;

  const iconMap = {
    think: '💭',
    pause: '⏸️',
    reflect: '🤔',
  };

  const colorMap = {
    think: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    pause: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    reflect: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
  };

  return (
    <div
      className="slide-content flex flex-col items-center justify-center min-h-[600px]"
      style={{ '--color-accent': accentColor } as React.CSSProperties}
    >
      <div className="max-w-2xl mx-auto text-center">
        <div className="text-6xl mb-6 animate-fade-in-up">
          {iconMap[type] || '💡'}
        </div>

        <h2 className="text-3xl font-serif font-bold mb-8 accent-text animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          {title}
        </h2>

        <div
          className={`${colorMap[type] || colorMap.think} border-2 rounded-xl p-8 animate-fade-in-up`}
          style={{ animationDelay: '0.2s' }}
        >
          {renderMarkdownPrompt(prompt)}
        </div>

        <p className="text-sm text-slate-500 dark:text-slate-400 mt-8 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
          Take a moment to consider this before continuing
        </p>
      </div>
    </div>
  );
}
